import { inject, injectable } from "inversify";
import { TYPES } from "@utils/inversifyJS/types";
import type { IRawFileRepository } from "@domain/repositories/IRawFileRepository";
import { useUploadStore } from "@presentation/stores/UploadStore";
import axios from "axios";

const uuidv4 = () => {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c: string) =>
        (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16)
    );
};

@injectable()
export class UploadRawFileService {
    @inject(TYPES.IRawFileRepository)
    private _rawFileRepository!: IRawFileRepository;

    private _fileCache: Map<string, File> = new Map();

    async uploadFile(file: File, folder: string, uploadId?: string, preCheckedSize?: number | null, uploadEventId?: string): Promise<void> {
        const id = uploadId || uuidv4();
        const cancelSource = axios.CancelToken.source();
        const { addUpload, updateProgress, setComplete, setFailed, setCancelled } = useUploadStore.getState();

        // If it's a new upload, add it to store and cache
        if (!uploadId) {
            addUpload(id, file.name, folder, cancelSource, "rawFile", file.size, uploadEventId);
            this._fileCache.set(id, file);
        } else {
            // Update cache in case we have a new file object (e.g. from re-select)
            this._fileCache.set(id, file);
            // If it's a resume, we might want to update the status to pending/uploading
            // and set the new cancel source
            useUploadStore.setState(state => ({
                activeUploads: {
                    ...state.activeUploads,
                    [id]: {
                        ...state.activeUploads[id],
                        status: "pending",
                        cancelSource,
                        error: undefined
                    }
                }
            }));
        }

        try {
            // 1. Check if file already exists on server
            let existingSize: number | null = null;

            if (preCheckedSize !== undefined) {
                existingSize = preCheckedSize;
            } else {
                existingSize = await this._rawFileRepository.checkFileExists(file.name, folder);
            }

            if (existingSize !== null) {
                if (existingSize === file.size) {
                    console.log(`File ${file.name} already exists with correct size. Skipping upload.`);
                    updateProgress(id, file.size, file.size);
                    setComplete(id);
                    return;
                } else if (existingSize < file.size) {
                    console.log(`File ${file.name} exists but is incomplete (${existingSize}/${file.size}). Re-uploading.`);
                    // Future: support range request to upload only the delta
                }
            }

            // 2. Start/Restart full upload
            updateProgress(id, 0, file.size);
            await this._rawFileRepository.uploadRawFile(
                file,
                folder,
                (percent) => {
                    updateProgress(id, (percent / 100) * file.size, file.size);
                },
                cancelSource.token
            );

            setComplete(id);
        } catch (error: unknown) {
            if (axios.isCancel(error)) {
                setCancelled(id);
            } else {
                setFailed(id, error instanceof Error ? error.message : "Upload failed");
            }
            throw error;
        }
    }

    async resumeUpload(fileOrId: File | string, folder?: string, uploadId?: string): Promise<void> {
        if (typeof fileOrId === "string") {
            const cachedFile = this._fileCache.get(fileOrId);
            if (!cachedFile) {
                throw new Error("File not found in cache. Please re-select the file.");
            }
            // If folder is not provided, we should ideally get it from store or keep it in cache
            // But resumeUpload is usually called with all info from the UI
            return this.uploadFile(cachedFile, folder!, fileOrId);
        } else {
            return this.uploadFile(fileOrId, folder!, uploadId);
        }
    }

    hasFileInCache(uploadId: string): boolean {
        return this._fileCache.has(uploadId);
    }

    async uploadMultipleFiles(files: File[], folder: string): Promise<void> {
        const uploadEventId = uuidv4();
        const uploadIdsByFile = new Map<File, string>();
        const { addUpload } = useUploadStore.getState();

        files.forEach((file) => {
            const uploadId = uuidv4();
            uploadIdsByFile.set(file, uploadId);
            addUpload(uploadId, file.name, folder, undefined, "rawFile", file.size, uploadEventId);
            this._fileCache.set(uploadId, file);
        });

        // 1. Batch check existence
        let existingSizes: Record<string, number | null> = {};
        try {
            const fileNames = files.map(f => f.name);
            // Optimistically check in batch. If it fails, existingSizes will be empty and individual checks will run.
            existingSizes = await this._rawFileRepository.checkFilesExist(fileNames, folder) || {};
        } catch (err) {
            console.warn("Batch file existence check failed, falling back to individual checks", err);
        }

        // 2. Concurrency limiting
        const CONCURRENCY_LIMIT = 6;
        const queue = [...files];

        const worker = async () => {
            while (queue.length > 0) {
                const file = queue.shift();
                if (!file) break;

                const preCheckedSize = existingSizes[file.name] !== undefined ? existingSizes[file.name] : undefined;
                const uploadId = uploadIdsByFile.get(file);
                if (!uploadId) continue;

                try {
                    await this.uploadFile(file, folder, uploadId, preCheckedSize, uploadEventId);
                } catch (err) {
                    // Error is already handled in uploadFile (setting status to failed)
                    // We just continue to next file
                }
            }
        };

        const workers = Array(Math.min(files.length, CONCURRENCY_LIMIT)).fill(null).map(() => worker());
        await Promise.all(workers);
    }
}
